import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Briefcase, Crown, Layers, Users, CalendarDays, BarChart3,
  Sparkles, Wallet, Clock, Target, Medal, Trophy, ArrowRight, ShoppingBag,
  ChevronRight
} from "lucide-react";
import clsx from "clsx";

import { get } from "@/lib/api-client";
import { storefrontApi } from "@/lib/extensions-api";
import { formatCurrency, formatNumber, formatDateTime } from "@/lib/format";
import { Skeleton } from "@/components/ui/Skeleton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { useAuthStore } from "@/store/auth-store";
import type { Order } from "@/types/order";

// --- Animations ---
const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 400, damping: 30 } }
};

export function DashboardPage() {
  const { user, setUser } = useAuthStore();

  useEffect(() => {
    get<any>("/auth/me").then((me) => setUser(me)).catch(() => { });
  }, [setUser]);

  const { data: stats, isLoading } = useQuery({
    queryKey: ["advanced-dashboard-stats"],
    queryFn: storefrontApi.dashboardStats,
  });

  const orders = useQuery({
    queryKey: ["orders", { page_size: 5 }],
    queryFn: () => get<Order[]>("/orders/orders/list", { page_size: 5 }),
  });

  return (
    <div className="space-y-8 pb-10">
      {/* 🌟 Premium Hero Header */}
      <header
        className="relative overflow-hidden rounded-2xl lg:rounded-[2rem] px-6 py-6 lg:px-8 lg:py-8 shadow-2xl flex flex-col md:flex-row items-center md:items-end justify-between gap-4"
        style={{ backgroundColor: '#000000' }}
      >
        {/* Background Glows adjusted for pure black background */}
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#68ff5c]/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#68ff5c]/10 rounded-full blur-[80px] translate-y-1/3 -translate-x-1/3 pointer-events-none" />

        <div className="relative z-10 w-full md:w-auto text-center md:text-left">
          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-[#68ff5c]/30 text-[#68ff5c] text-[10px] sm:text-xs font-semibold uppercase tracking-widest mb-3 backdrop-blur-md shadow-sm">
            <Sparkles className="h-3 w-3 text-[#68ff5c]" /> Member Portal
          </div>

          {/* Welcome Text */}
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight">
            Welcome back, <br className="hidden md:block" />
            <span className="text-[#68ff5c] ml-1 md:ml-0 drop-shadow-sm">
              {user?.full_name || "Member"}
            </span>
          </h1>
        </div>

        {/* Account ID Card */}
        <div className="relative z-10 w-full md:w-auto bg-white/5 border border-[#68ff5c]/30 p-3 sm:p-4 rounded-xl lg:rounded-2xl backdrop-blur-md text-center md:text-right flex items-center justify-between md:flex-col md:items-end md:justify-end shadow-sm">
          <div className="text-xs sm:text-sm lg:text-base font-semibold text-[#68ff5c] uppercase tracking-widest mb-1">
            Account ID
          </div>
          <div className="text-lg sm:text-xl lg:text-2xl font-mono font-black text-white tracking-wider">
            {user?.user_id}
          </div>
        </div>
      </header>

      {isLoading || !stats ? (
        <div className="space-y-4">
          {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
        </div>
      ) : (
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-8">

          {/* 💼 SECTION: ACCOUNT STATUS */}
          <section>
            <SectionTitle title="Account Status" />
            <div className="flex flex-col gap-4">
              <WideColorfulCard
                icon={Wallet} label="Withdrawable Balance" value={formatCurrency(stats.wallet_withdrawable)}
                colorTheme="emerald"
              />
              <WideColorfulCard
                icon={Clock} label="Pending Balance" value={formatCurrency(stats.wallet_pending)}
                colorTheme="amber"
              />
            </div>
          </section>

          {/* 📊 SECTION: BUSINESS STATUS */}
          <section>
            <SectionTitle title="Business Status" />
            <div className="flex flex-col gap-4">
              <Link to="/dashboard/entries" className="block focus:outline-none">
                <motion.div variants={itemVariants} className="relative overflow-hidden rounded-[1.25rem] bg-emerald-500 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer text-white p-5">
                  <div className="text-center mb-3">
                    <h3 className="text-xl sm:text-2xl font-semibold tracking-wide">Auto Club</h3>
                  </div>

                  {/* Thin divider line */}
                  <div className="w-full h-[1px] bg-white/30 mb-4" />

                  <div className="flex justify-between items-end">
                    <div className="text-left">
                      <div className="text-3xl sm:text-4xl font-semibold leading-none mb-1">{formatNumber(stats.current_club_points)}</div>
                      <div className="text-sm font-medium text-white/90">My Points</div>
                    </div>

                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 rounded-2xl bg-blue-500 shadow-inner flex items-center justify-center mb-1.5 border border-white/10">
                        {/* Golden Star Icon */}
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#FBBF24" />
                        </svg>
                      </div>
                      <div className="text-[10px] font-medium text-white text-center leading-tight">
                        Club<br />Member
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Link>
              <WideColorfulSplitCard
                icon={Target} label="Total BV (Lifetime)" colorTheme="cyan"
                leftTitle="(Own)" leftValue={formatNumber(stats.lifetime_bv.own)}
                rightTitle="Team Points" rightValue={formatNumber(stats.lifetime_bv.team)}
              />
              <WideColorfulSplitCard
                icon={Users} label="Total Users" colorTheme="violet"
                leftTitle="1st Gen Member" leftValue={formatNumber(stats.referrals.direct)}
                rightTitle="Team Members" rightValue={formatNumber(stats.referrals.team)}
              />
              <WideColorfulSplitCard
                icon={Sparkles} label="Star Member Point Pool" colorTheme="rose"
                leftTitle="Own Points (This Month)" leftValue={formatNumber(stats.current_month_bv.own)}
                rightTitle="Team Points (This Month)" rightValue={formatNumber(stats.current_month_bv.team)}
              />
            </div>
          </section>

          {/* 💰 SECTION: INCOME AT A GLANCE */}
          <section>
            <SectionTitle title="Income at a Glance" />
            <div className="flex flex-col gap-4">
              <WideColorfulCard icon={Briefcase} label="Marketing Bonus" value={formatCurrency(stats.marketing_bonus)} colorTheme="blue" />
              <WideColorfulCard icon={CalendarDays} label="Daily Income" value={formatCurrency(stats.daily_income)} colorTheme="amber" />
              <Link to="/dashboard/referrals" className="block focus:outline-none transition-transform hover:-translate-y-1">
                <WideColorfulCard
                  icon={BarChart3}
                  label="Generation Income"
                  value={formatCurrency(stats.generation_income)}
                  colorTheme="emerald"
                />
              </Link>
              <WideColorfulCard icon={Trophy} label="Club Income" value={formatCurrency(stats.club_income)} colorTheme="violet" />
              <WideColorfulCard icon={Crown} label="Star Commission" value={formatCurrency(stats.star_member_income)} colorTheme="rose" />
              <WideColorfulCard icon={Medal} label="Designation Bonus" value={formatCurrency(stats.designation_income)} colorTheme="cyan" />
            </div>
          </section>

        </motion.div>
      )}

      {/* 🚀 Quick Actions & Recent Orders */}
      <div className="grid gap-4 lg:gap-6 lg:grid-cols-2 pt-4">
        <Card className="border-0 shadow-elevate-1 bg-white rounded-2xl lg:rounded-[1.5rem] overflow-hidden h-fit">
          <CardHeader className="border-b border-surface-100 bg-surface-50/50">
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardBody className="p-4 sm:p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <QuickLink to="/dashboard/catalog" icon={Layers} title="Catalog" desc="Purchase products" color="bg-blue-50 text-blue-600" />
              <QuickLink to="/dashboard/referrals" icon={Users} title="Network" desc="View your downline" color="bg-violet-50 text-violet-600" />
              <QuickLink to="/dashboard/star" icon={Crown} title="Star Member" desc="Check offers & targets" color="bg-amber-50 text-amber-600" />
              <QuickLink to="/dashboard/wallet/withdraw" icon={Briefcase} title="Withdraw" desc="Transfer to bank" color="bg-emerald-50 text-emerald-600" />
            </div>
          </CardBody>
        </Card>

        <Card className="border-0 shadow-elevate-1 bg-white rounded-2xl lg:rounded-[1.5rem] flex flex-col overflow-hidden h-fit">
          <CardHeader className="border-b border-surface-100 bg-surface-50/50 flex items-center justify-between">
            <CardTitle>Recent Orders</CardTitle>
            <Link to="/dashboard/orders" className="text-sm font-semibold text-brand-600 hover:text-brand-700 flex items-center gap-1 transition-colors">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </CardHeader>
          <CardBody className="p-0 flex-1">
            {orders.isLoading ? (
              <div className="p-5 space-y-4">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}
              </div>
            ) : orders.data && orders.data.length ? (
              <ul className="divide-y divide-surface-100/80">
                {orders.data.slice(0, 4).map((o) => (
                  <li key={o.order_id} className="p-4 flex items-center justify-between hover:bg-surface-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-surface-100 text-surface-500 flex items-center justify-center">
                        <ShoppingBag className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-bold text-surface-900 text-sm">{o.order_id}</div>
                        <div className="text-xs text-surface-500 font-medium">{formatDateTime(o.placed_at)}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-base text-surface-900 tracking-tight">{formatCurrency(o.total)}</div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-surface-400 mt-0.5">{o.status.replace("_", " ")}</div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-center p-8">
                <ShoppingBag className="h-10 w-10 text-surface-200 mb-3" />
                <div className="text-surface-500 font-medium">No orders found.</div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

// --- Luxury UI Components ---

function SectionTitle({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-3 mb-4 pl-2">
      <div className="w-1.5 h-6 bg-brand-500 rounded-full" />
      <h2 className="text-xl font-bold text-surface-900 tracking-tight">{title}</h2>
    </div>
  );
}

const COLOR_THEMES = {
  blue: { bg: "bg-gradient-to-r from-blue-500 to-blue-600", iconBg: "bg-white/20", glow: "shadow-blue-500/30" },
  amber: { bg: "bg-gradient-to-r from-amber-400 to-orange-500", iconBg: "bg-white/20", glow: "shadow-orange-500/30" },
  emerald: { bg: "bg-gradient-to-r from-emerald-400 to-emerald-600", iconBg: "bg-white/20", glow: "shadow-emerald-500/30" },
  rose: { bg: "bg-gradient-to-r from-rose-400 to-rose-600", iconBg: "bg-white/20", glow: "shadow-rose-500/30" },
  cyan: { bg: "bg-gradient-to-r from-cyan-400 to-cyan-600", iconBg: "bg-white/20", glow: "shadow-cyan-500/30" },
  violet: { bg: "bg-gradient-to-r from-violet-500 to-purple-600", iconBg: "bg-white/20", glow: "shadow-purple-500/30" },
};

function WideColorfulCard({ icon: Icon, label, value, colorTheme }: any) {
  const theme = COLOR_THEMES[colorTheme as keyof typeof COLOR_THEMES];

  return (
    <motion.div variants={itemVariants} className={clsx(
      "group relative overflow-hidden rounded-[1.25rem] p-4 sm:p-5 shadow-md hover:shadow-lg transition-all cursor-default flex items-center justify-between text-white hover:-translate-y-1",
      theme.bg, theme.glow
    )}>
      <Icon className="absolute -right-4 -bottom-4 w-32 h-32 opacity-10 rotate-12 group-hover:scale-110 transition-transform duration-500 pointer-events-none" />
      <div className="flex items-center gap-4 relative z-10">
        <div className={clsx("w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-sm backdrop-blur-sm", theme.iconBg)}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="font-bold text-white/90 sm:text-lg tracking-wide">{label}</div>
      </div>
      <div className="text-xl sm:text-2xl font-extrabold text-white tracking-tight relative z-10 drop-shadow-sm">
        {value}
      </div>
    </motion.div>
  );
}

function WideColorfulSplitCard({ icon: Icon, label, leftTitle, leftValue, rightTitle, rightValue, colorTheme }: any) {
  const theme = COLOR_THEMES[colorTheme as keyof typeof COLOR_THEMES];

  return (
    <motion.div variants={itemVariants} className={clsx(
      "group relative overflow-hidden flex flex-col sm:flex-row sm:items-center justify-between rounded-[1.25rem] p-4 sm:p-5 shadow-md hover:shadow-lg transition-all cursor-default gap-4 sm:gap-0 text-white hover:-translate-y-1",
      theme.bg, theme.glow
    )}>
      <Icon className="absolute -right-4 -bottom-4 w-48 h-48 opacity-[0.08] rotate-12 group-hover:scale-110 transition-transform duration-500 pointer-events-none" />

      <div className="flex items-center gap-4 sm:w-5/12 relative z-10">
        <div className={clsx("w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-sm backdrop-blur-sm", theme.iconBg)}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="font-bold text-white/90 sm:text-lg tracking-wide">{label}</div>
      </div>

      <div className="flex flex-1 items-center justify-between sm:justify-end gap-6 sm:gap-10 pl-16 sm:pl-0 relative z-10">
        <div className="text-left sm:text-right">
          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-white/70">{leftTitle}</div>
          <div className="text-xl sm:text-2xl font-extrabold text-white tracking-tight mt-0.5 drop-shadow-sm">{leftValue}</div>
        </div>

        <div className="w-px h-10 bg-white/20 shrink-0" />

        <div className="text-left sm:text-right">
          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-white/70">{rightTitle}</div>
          <div className="text-xl sm:text-2xl font-extrabold text-white tracking-tight mt-0.5 drop-shadow-sm">{rightValue}</div>
        </div>
      </div>
    </motion.div>
  );
}

function QuickLink({ to, icon: Icon, title, desc, color }: any) {
  return (
    <Link to={to} className="group flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-[1rem] bg-surface-50/50 hover:bg-white border border-transparent hover:border-surface-200 hover:shadow-sm transition-all">
      <div className={clsx("w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-105", color)}>
        <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-surface-900 text-sm sm:text-base leading-tight">{title}</div>
        <div className="text-[10px] sm:text-xs text-surface-500 truncate mt-0.5">{desc}</div>
      </div>
      <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-surface-300 group-hover:text-brand-500 group-hover:translate-x-1 transition-all" />
    </Link>
  );
}